import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import SideBar from "../components/common/SideBar";
import Navbar from "../components/common/Navbar";
import ScoreBoard from "../components/createPost/ScoreBoard";
import Footer from "../components/common/Footer";
import ProgressBar from "../components/common/ProgressBar";
import PostSelection from "../components/createPost/PostSelection";
import TipsSection from "../components/createPost/TipsSection";
import PreviewModal from "../components/createPost/components/PreviewModal";
import WelcomeModal from "../components/common/WelcomeModal";
import FeelimThinkingAnimation from "../components/common/FeelimThinkingAnimation";
import FeelimImageAnimation from "../components/common/FeelimImageAnimation";
import ScheduleModal from "../components/common/ScheduleModal";
import UserAccountModal from "../components/common/UserAccountModal";
import PublishBottomSheet from "../components/common/PublishBottomSheet";
import Button from "../components/common/Button";
import { convertHtmlToLinkedIn } from "../components/createPost/linkedinCopy";
import { heuristicPlan } from "../components/createPost/components/CarouselSection";
import { formatLocalDateTime, getTimezone } from "../utils/dates";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { mediaTitleFrom, mediaFilenameFrom, titleFromFilename } from "../utils/filename";
import splitOwnText from "../utils/splitOwnText";
import useUserEmail from "../utils/useUserEmail";
import useLinkedInAccounts from "../utils/useLinkedInAccounts";
import { readError } from "../utils/apiError";
import { apiFetch, authHeaders } from "../utils/apiFetch";
import { CreateFlowProvider } from "../flow/CreateFlowContext";
import RegenerateImageModal from "../flow/RegenerateImageModal";
import StaleMediaModal from "../flow/StaleMediaModal";
import { imageFingerprint, carouselFingerprint } from "../flow/mediaFingerprint";
import { extractPhrases } from "../utils/extractPhrases";
import useVideoUpload from "../video/useVideoUpload";
import { attachVideo, deleteVideo } from "../video/videoApi"
import useUserPlan from "../utils/useUserPlan";

const MAX_GENERATED_IMAGES = 5;

// Leaving the page with a video that never reached a saved post: free its quota
const discardIfUnattached = (videoRef, attachedRef) => {
  const v = videoRef.current;
  if (v.status === "idle") return;
  if (v.video && v.video.video_id === attachedRef.current) return;
  v.discardVideo();
};

const Home = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [selectedPostContent, setSelectedPostContent] = useState("");
  const [previewText, setPreviewText] = useState("");
  const [previewImages, setPreviewImages] = useState([]);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isGeneratedImageSelected, setIsGeneratedImageSelected] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [isInsightsLoading, setIsInsightsLoading] = useState(false);
  const [isImageGenerating, setIsImageGenerating] = useState(false);
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [promptText, setPromptText] = useState("");
  const [hasOwnText, setHasOwnText] = useState(null); 
  const [ownTextDraft, setOwnTextDraft] = useState("");
  const [postLanguage, setPostLanguage] = useState(null); // null = follow the app language
  const [generatedImages, setGeneratedImages] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [generatedHooks, setGeneratedHooks] = useState(null);
  const [generatedPosts, setGeneratedPosts] = useState(null);
  const [errorHeader, setErrorHeader] = useState("");
  const [imageLocked, setImageLocked] = useState(false);
  const [mobileStep, setMobileStep] = useState(1);
  const [desktopStage, setDesktopStage] = useState("text");
  const generateImageRef = useRef(null);
  const handledKey = useRef(null);
  const [textConfirmed, setTextConfirmed] = useState(false);
  const [postSegments, setPostSegments] = useState(null);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [postSaved, setPostSaved] = useState(false);
  const [editPostId, setEditPostId] = useState(null); // Edit mode — holds the id of the post being edited (null = new post / POST, set = editing / PUT)
  const [originalImageUrl, setOriginalImageUrl] = useState(null);
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState(false);
  const [savedPostId, setSavedPostId] = useState(null);
  const [savedImageUrl, setSavedImageUrl] = useState(null);
  const [linkedInTarget, setLinkedInTarget] = useState(null);
  const email = useUserEmail();
  const { accounts, isConnected: isLinkedInConnected } = useLinkedInAccounts(email);
  const [isPublishSheetOpen, setIsPublishSheetOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);
  const [editPostDateTime, setEditPostDateTime] = useState(null);
  const [editPostStatus, setEditPostStatus] = useState(null);
  const [contentType, setContentType] = useState(null);
  const [carouselSlides, setCarouselSlides] = useState([]);
  const [carouselPreview, setCarouselPreview] = useState(null);
  const [documentFile, setDocumentFile] = useState(null);
  const [savedDocumentUrl, setSavedDocumentUrl] = useState(null);
  const carouselExportRef = useRef(null);
  const [documentFilename, setDocumentFilename] = useState(null);
  const [hasMedia, setHasMedia] = useState(null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);
  const [carouselPdfFile, setCarouselPdfFile] = useState(null);
  const [showMediaBackConfirm, setShowMediaBackConfirm] = useState(false);
  const [imageMeta, setImageMeta] = useState(null);
  const [carouselMeta, setCarouselMeta] = useState(null);
  const [regenOpen, setRegenOpen] = useState(false);
  const [staleType, setStaleType] = useState(null);
  const [preLogoImages, setPreLogoImages] = useState({});
  const videoUpload = useVideoUpload();
  const plan = useUserPlan();
  const [savedVideo, setSavedVideo] = useState(null);                 // the video of the post being edited
  const [savedVideoRemoved, setSavedVideoRemoved] = useState(false);  // the user pressed Remove (applied on Save)
  const videoRef = useRef(videoUpload);
  videoRef.current = videoUpload;
  const attachedVideoRef = useRef(null);   // video_id already attached to a saved post
  const pendingAttachRef = useRef(null);   // { postId, videoId }: post saved, video not attached yet
  const deletedVideoRef = useRef(null);    // old video_id already deleted after a replace/remove

  // What the screens read: the upload plus the saved video of the post being edited
  const videoFlow = {
    ...videoUpload,
    saved: savedVideoRemoved ? null : savedVideo,
    savedRemoved: savedVideoRemoved,
    removeSaved: () => setSavedVideoRemoved(true),
    restoreSaved: () => setSavedVideoRemoved(false),
    // Save / Schedule unlock: a new upload finished, or an existing video is kept or removed
    ready: videoUpload.status === "done" || (videoUpload.status === "idle" && !!savedVideo),
  };

  const startVideoUpload = ({ file, duration, poster }) =>
    videoUpload.startUpload({
      email,
      plan,
      target: linkedInTarget || "personal",
      file,
      duration,
      poster,
    });

  // Closing or leaving the editor without saving deletes the video on the server
  useEffect(() => () => discardIfUnattached(videoRef, attachedVideoRef), []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // const scores = [
  //   t("scoreBoard.attention"),
  //   t("scoreBoard.industry"),
  //   t("scoreBoard.reading"),
  //   t("scoreBoard.inclusion"),
  //   t("scoreBoard.forecast"),
  // ];
  
  const activeGeneratedImgUrl = generatedImages[selectedImageIndex] || null;
  const preLogoKey = isGeneratedImageSelected ? selectedImageIndex : "upload";
  const preLogoImage = preLogoImages[preLogoKey] ?? null;

  const releasePreLogoImages = () => {
    Object.values(preLogoImages).forEach((entry) => {
      if (entry?.url?.startsWith("blob:")) URL.revokeObjectURL(entry.url);
    });
    setPreLogoImages({});
  };

  const releasePreLogoImageAt = (key) => {
    const entry = preLogoImages[key];
    if (entry?.url?.startsWith("blob:")) URL.revokeObjectURL(entry.url);
    setPreLogoImages((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const discardUnattachedVideo = () => {
    if (videoUpload.status === "idle") return;
    if (videoUpload.video?.video_id === attachedVideoRef.current) videoUpload.clearVideo();
    else videoUpload.discardVideo();
    attachedVideoRef.current = null;
    pendingAttachRef.current = null;
  };

  const resetCreationState = () => {
    discardUnattachedVideo();
    setSavedVideo(null);
    setSavedVideoRemoved(false);
    deletedVideoRef.current = null;
    releasePreLogoImages();
    releaseGeneratedImages();
    setSelectedPostContent("");
    setPreviewText("");
    setPreviewImages([]);
    setIsPreviewOpen(false);
    setIsGeneratedImageSelected(false);
    setGeneratedHooks(null);
    setGeneratedPosts(null);
    setGeneratedImages([]);
    setSelectedImageIndex(0);
    setImageLocked(false);
    setTextConfirmed(false);
    setPostSegments(null);
    setSelectedPostId(null);
    setMobileStep(1);
    setResetKey((prev) => prev + 1);
    setPostSaved(false);
    setPromptText("");
    setEditPostId(null);
    setEditPostDateTime(null);
    setSavedPostId(null);
    setSavedImageUrl(null);
    setEditPostStatus(null);
    setOriginalImageUrl(null);
    setCarouselSlides([]);
    setDocumentFile(null);
    setSavedDocumentUrl(null);
    setDocumentFilename(null);
    setHasMedia(null);
    setDraftSaved(false);
    setContentType(null);
    setHasOwnText(null);
    setOwnTextDraft("");
    setImageMeta(null);
    setCarouselMeta(null); 
  };

  const handleResetText = () => {
    setGeneratedHooks(null);
    setGeneratedPosts(null);
    setSelectedPostContent("");
    setPreviewText("");
    setPostSegments(null);
    setSelectedPostId(null);
    setCarouselSlides([]);
    setPostSaved(false);
    setResetKey((prev) => prev + 1);
    setImageMeta(null);
    setCarouselMeta(null);
  };

  useEffect(() => {
    // each navigation has a unique key — handle it once, never again
    if (handledKey.current === location.key) return;
    handledKey.current = location.key;

    if (location.state?.reset) {
      resetCreationState();
      return;
    }

    if (location.state?.editPost) {
      const p = location.state.editPost;
      const hashtagLine = (p.hashtags || []).map((h) => `#${h}`).join(" ");
      const fullText = [p.hook, p.body, p.cta, hashtagLine]
        .filter(Boolean)
        .join("\n");

      setSelectedPostContent(fullText);
      setPreviewText(fullText);
      setPostSegments({
        hook: p.hook,
        cuerpo: p.body,
        cta: p.cta,
        hashtags: p.hashtags,
      });
      setEditPostId(p.id);
      setEditPostDateTime(p.publicationDateTime || null);
      setEditPostStatus(p.status || null);
      setLinkedInTarget(p.target || null);
      setSelectedPostId("hook_1_disruptivo");

      if (p.imageUrl) {
        setContentType("single");
        setGeneratedImages([p.imageUrl]);
        setSelectedImageIndex(0);
        setIsGeneratedImageSelected(true);
        setImageLocked(true);
        setOriginalImageUrl(p.imageUrl);
        setTextConfirmed(true);
      } else if (p.documentUrl) {
        setContentType("document");
        setSavedDocumentUrl(p.documentUrl);
        setDocumentFilename(p.documentFilename || null);
        setDocumentFile(null);
        setTextConfirmed(true);
      } else if (p.videoUrl) {
        setContentType("video");
        setSavedVideo({
          video_id: p.videoId,
          video_url: p.videoUrl,
          video_poster_url: p.videoPosterUrl || "",
        });
        setSavedVideoRemoved(false);
        setTextConfirmed(true);
      } else {
        setContentType("single");
        setTextConfirmed(false);
      }
      setMobileStep(3);
    }
  }, [location.key]);   // ← key, not state

  useEffect(() => {
    const hasSeenWelcome = localStorage.getItem("welcome_seen");
    if (!hasSeenWelcome) {
      setShowWelcome(true);
    }
  }, []);

  useEffect(() => {
    if (generatedHooks && mobileStep === 1) {
      setMobileStep(2);
    }
  }, [generatedHooks]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [mobileStep]);

  const handleStart = () => {
    localStorage.setItem("welcome_seen", "true");
    setShowWelcome(false);
  };

  const handleImagesChange = (files) => {
    if (imageLocked) return;
    setPostSaved(false);
    setPreviewImages(files);
    setIsGeneratedImageSelected(false);
    setImageLocked(true);
  };

  const handleGenerateSelect = () => {
    if (imageLocked) return;
    setPostSaved(false);
    setPreviewImages([]);
    setIsGeneratedImageSelected(true);
    setImageLocked(true);
  };

  const handleRegenerateText = () => {
    // No longer needed — kept as placeholder if future logic is added
  };

  const handleImageGenerated = (url, format) => {
    setImageMeta({ format, fingerprint: imageFingerprint(postSegments, format) });
    setGeneratedImages((prev) => {
      if (prev.length >= MAX_GENERATED_IMAGES) return prev;
      const updated = [...prev, url];
      setPostSaved(false);
      setSelectedImageIndex(updated.length - 1);
      return updated;
    });
  };

  const languageMap = {
    en: "English",
    es: "Español",
    fr: "Français",
    it: "Italiano",
  };

  // An explicit choice wins; otherwise follow the interface language.
  // i18n.language can be "en-US", hence the split.
  const resolvedPostLanguage = () =>
    languageMap[postLanguage || i18n.language.split("-")[0]] || "English";

  const handleGeneratePosts = async (textBase) => {
    setIsInsightsLoading(true);
    setErrorHeader("");
    try {
      const generateUrl = `${process.env.REACT_APP_API_BASE_URL}/api/generate`;
      const response = await axios.post(
        generateUrl,
        {
          texto_base: textBase,
          idioma: resolvedPostLanguage(),
        },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            ...(await authHeaders(generateUrl)),
          },
        },
      );
      const data = response.data.response;
      setGeneratedHooks(data.resumen_hooks);
      setGeneratedPosts(data.posts_completos);
    } catch (error) {
      console.error("Error generating posts:", error);
      setErrorHeader(t("errors.generateFailed"));
    } finally {
      setIsInsightsLoading(false);
    }
  };

  const handleGenerateFromDocument = async (file) => {
    setIsInsightsLoading(true);
    setErrorHeader("");
    try {
      const formData = new FormData();
      formData.append("document", file);
      formData.append("idioma", resolvedPostLanguage());

      // No Content-Type header on purpose — axios must set the multipart
      // boundary itself. Setting it by hand produces a request the backend
      // can't parse.
      const documentUrl = `${process.env.REACT_APP_API_BASE_URL}/api/generate_from_document`;
      const response = await axios.post(documentUrl, formData, {
        headers: await authHeaders(documentUrl),
      });

      const data = response.data.response;
      setGeneratedHooks(data.resumen_hooks);
      setGeneratedPosts(data.posts_completos);
    } catch (error) {
      console.error("Error generating from document:", error);
      const status = error?.response?.status;
      setErrorHeader(
        status === 400
          ? t("summary.errors.failed")
          : t("errors.generateFailed"),
      );
    } finally {
      setIsInsightsLoading(false);
    }
  };

  const handleGenerateIdeasRequest = async (hookId) => {
    if (!generatedPosts) return;
    const mapping = {
      hook_1_disruptivo: "opcion_1_disruptivo_corto",
      hook_2_valor: "opcion_2_valor_medio",
      hook_3_storytelling: "opcion_3_storytelling_largo",
    };
    const postKey = mapping[hookId];
    const postData = generatedPosts[postKey];
    if (!postData) return;
    const fullPost = `${postData.hook}\n${postData.cuerpo}\n${postData.cta}\n${postData.hashtags.map((h) => `#${h}`).join(" ")}`;
    setSelectedPostContent(fullPost);
    setPostSegments({
      hook: postData.hook,
      cuerpo: postData.cuerpo,
      cta: postData.cta,
      hashtags: postData.hashtags,
    });
  };

  // The user brought their own text: split it into the same four blocks the
  // generator returns, and send it to the editor the same way a generated post goes.
  const handleUseOwnText = (text) => {
    const segments = splitOwnText(text);
    const hashtagLine = segments.hashtags.map((h) => `#${h}`).join(" ");
    setSelectedPostContent(
      [segments.hook, segments.cuerpo, segments.cta, hashtagLine]
        .filter(Boolean)
        .join("\n")
    );
    setPostSegments(segments);
    setSelectedPostId(null);
    setPostSaved(false);
  };

  const handleDeleteImage = () => {
    releasePreLogoImages();
    releaseGeneratedImages();
    setPostSaved(false);
    setPreviewImages([]);
    setIsGeneratedImageSelected(false);
    setGeneratedImages([]);
    setSelectedImageIndex(0);
    setImageLocked(false);
    setTextConfirmed(false);
    setOriginalImageUrl(null);
    setImageMeta(null);
  };

  // Generated images are object URLs. In edit mode the array holds a remote
  // URL instead, which must not be revoked — hence the blob: check.
  const releaseGeneratedImages = () => {
    generatedImages.forEach((url) => {
      if (typeof url === "string" && url.startsWith("blob:")) URL.revokeObjectURL(url);
    });
  };

  const handleUnlockStyle = () => {
    setImageLocked(false);
  };

  // Plan the slides once, when the carousel starts. Later text changes do NOT
  // re-plan them: that would overwrite the user's edits.
  useEffect(() => {
    if (contentType === "carousel" && postSegments && carouselSlides.length === 0) {
      setCarouselSlides(heuristicPlan(postSegments));
    }
  }, [contentType, postSegments, carouselSlides.length]);

  // Called when the user chooses to create the carousel: plan from the text as it is now
  const handleCreateCarousel = () => {
    if (!postSegments) return;
    setCarouselSlides(heuristicPlan(postSegments));
    setCarouselMeta(carouselFingerprint(postSegments));
  };

  // One place that knows how to get bytes out of whichever media is active.
  // Returns null for text-only posts, which the save path already tolerates.
  const getMediaFile = async () => {
    if (hasMedia === false) return null;
    if (contentType === "video") return null;   // the video goes straight to Azure Blob, never in this request
    if (contentType === "carousel") {
      if (carouselPdfFile) return carouselPdfFile;
      return (await carouselExportRef.current?.(mediaFilenameFrom(postSegments?.hook))) || null;
    }
    if (contentType === "document") {
      return documentFile;
    }
    // image
    if (isGeneratedImageSelected && activeGeneratedImgUrl) {
      if (activeGeneratedImgUrl === originalImageUrl) return null;  // unchanged, skip re-upload
      try {
        const res = await fetch(activeGeneratedImgUrl);
        const blob = await res.blob();
        return new File(
          [blob],
          mediaFilenameFrom(postSegments?.hook, { prefix: "Image", ext: "png" }),
          { type: blob.type || "image/png" }
        );
      } catch (err) {
        console.error("Failed to fetch generated image:", err);
        return null;
      }
    }
    if (previewImages.length > 0) {
      return previewImages[0];
    }
    return null;
  };

  const stripDividers = (html) => {
    const tmp = document.createElement("div");
    tmp.innerHTML = html || "";
    tmp.querySelectorAll("[data-divider]").forEach((el) => el.remove());
    return tmp.innerHTML;
  };

  const handleDocumentSelect = (file) => {
    setPostSaved(false);
    setDocumentFile(file);
    setDocumentFilename(file.name);
    setTextConfirmed(true);
  };

  const handleDocumentDelete = () => {
    setPostSaved(false);
    setDocumentFile(null);
    setDocumentFilename(null);
    setSavedDocumentUrl(null);
  };

  // Kept for a future "your media will be lost" confirmation (not triggered by Back now)
  // eslint-disable-next-line no-unused-vars
  const hasMediaAttached =
    imageLocked ||
    !!documentFilename ||
    contentType === "carousel" ||
    generatedImages.length > 0 ||
    previewImages.length > 0;

  // Kept for a future "your media will be lost" confirmation (not triggered by Back now)
  // eslint-disable-next-line no-unused-vars
  const goBackToMediaTypeStep = () => {
    handleDeleteImage();
    handleDocumentDelete();
    setCarouselPdfFile(null);
    setCarouselSlides([]);
    setContentType(null);
    setTextConfirmed(false);
    setShowMediaBackConfirm(false);
    setMobileStep(5);
  };

  const handleDownload = async () => {
    try {
      const zip = new JSZip();
      zip.file("post.txt", convertHtmlToLinkedIn(stripDividers(previewText)) || "No content");

      const mediaFile = await getMediaFile();
      if (mediaFile) {
        zip.file(mediaFile.name || "media", mediaFile);
      }

      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, "post-download.zip");
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  const handleSaveAsDraft = async () => {
    setIsSavingDraft(true);
    try {
      await handleSavePost({});
      setDraftSaved(true);
    } catch (err) {
      setErrorHeader(err?.message || t("errors.saveFailed"));
    } finally {
      setIsSavingDraft(false);
    }
  };

  // Called before we hand the browser to LinkedIn. OAuth leaves the SPA
  // entirely, so anything still in component state is gone — save it first.
  // Returning false aborts the redirect, which is the point: losing the
  // work is worse than not connecting.
  const handleBeforeConnectLinkedIn = async () => {
    if (!previewText) return true;
    if (!postSaved) {
      try {
        await handleSavePost({});
      } catch (err) {
          setErrorHeader(err?.message || t("errors.saveBeforeConnect"));
        return false;
      }
    }
    sessionStorage.setItem("afterLinkedInConnect", "/draft");
    return true;
  };

  // The posts endpoint rejects a SAS token or query string in the URLs
  const plainUrl = (url) => (url || "").split("?")[0];

  // API 6. Required: it is how the backend knows which video belongs to the post.
  const attachVideoToPost = async (postId) => {
    const videoId = videoUpload.video.video_id;
    if (attachedVideoRef.current === videoId) return;   // saved twice: already attached
    pendingAttachRef.current = { postId, videoId };
    try {
      await attachVideo(email, videoId, postId);
    } catch (err) {
      console.error("Attach video failed:", err.code);
      throw new Error(t("video.errors.attach"));
    }
    attachedVideoRef.current = videoId;
    pendingAttachRef.current = null;
  };

  // Section F: the old video is deleted only after the post no longer points to it
  const deleteReplacedVideo = async () => {
    if (!editPostId || !savedVideo) return;
    if (deletedVideoRef.current === savedVideo.video_id) return;
    try {
      await deleteVideo(email, savedVideo.video_id);
      deletedVideoRef.current = savedVideo.video_id;
    } catch (err) {
      if (err.code === "VIDEO_BUSY") throw new Error(t("video.errors.busy"));
      console.error("Deleting the old video failed:", err.code); // the post is already saved
    }
  };

  const handleSavePost = async ({ publicationDateTime, timezone, target }) => {
    if (!previewText) return;

    const isVideoPost = contentType === "video";
    if (isVideoPost && !videoFlow.ready) throw new Error(t("video.waitUpload"));
    const hasNewVideo = isVideoPost && videoUpload.status === "done" && !!videoUpload.video;
    const removingSaved = isVideoPost && !hasNewVideo && savedVideoRemoved && !!savedVideo;

    // The post was saved but its video could not be attached: only retry the attach,
    // otherwise saving again would create a second post
    const pending = pendingAttachRef.current;
    if (hasNewVideo && pending && pending.videoId === videoUpload.video.video_id) {
      await attachVideoToPost(pending.postId);
      await deleteReplacedVideo();
      setPostSaved(true);
      setSavedPostId(pending.postId);
      return;
    }

    const formData = new FormData();
    formData.append("post", convertHtmlToLinkedIn(stripDividers(previewText)));
    const resolvedTarget = target || linkedInTarget;
    if (target) setLinkedInTarget(target);
    if (publicationDateTime) formData.append("target", resolvedTarget);
    formData.append("hook", postSegments?.hook || "");
    formData.append("body", postSegments?.cuerpo || "");
    formData.append("cta", postSegments?.cta || "");
    formData.append("hashtags", JSON.stringify(postSegments?.hashtags || []));
    if (publicationDateTime) formData.append("timezone", timezone);
    if (editPostId) {
      formData.append("id", editPostId);
      if (publicationDateTime) {
        formData.append("publication_date", publicationDateTime);
        if (editPostStatus === "Published") {
          formData.append("status", "Scheduled");
        }
      }
    } else {
      if (publicationDateTime) formData.append("publication_date_time", publicationDateTime);
    }

    const mediaFile = await getMediaFile();

    // Editing a post that has a video and saving a new image/document instead: the PUT must
    // clear the video (otherwise 400) and the old video is deleted afterwards
    const replacesSavedVideo = !!editPostId && !!savedVideo && !isVideoPost && !!mediaFile;

    // Sections C and F. Keeping the saved video sends no video fields: it stays as it is.
    if (hasNewVideo) {
      formData.append("video_id", videoUpload.video.video_id);
      formData.append("video_url", plainUrl(videoUpload.video.blob_url));
      formData.append("video_poster_url", plainUrl(videoUpload.video.poster_url));
    } else if (removingSaved || replacesSavedVideo) {
      formData.append("video_id", "");
    }

    // Media already on the server (editing without changing it) is fine.
    const hasStoredMedia = !!(savedDocumentUrl || savedImageUrl || originalImageUrl);

    if (hasMedia === true && !mediaFile && !hasStoredMedia && !(isVideoPost && videoFlow.ready)) {
      throw new Error(t("document.errors.mediaMissing"));
    }

    if (mediaFile) {
      const field = contentType === "carousel" || contentType === "document" ? "document" : "image";
      formData.append(field, mediaFile, mediaFile.name || "media");
    }

    // Use PUT for existing posts, POST for new ones
    // PUT endpoint: REACT_APP_UPDATE_POST_URL (id goes in body, not the URL)
    const isEditing = !!editPostId;
    const url = isEditing
      ? process.env.REACT_APP_UPDATE_POST_URL
      : process.env.REACT_APP_SAVE_POST_URL;
    const method = isEditing ? "PUT" : "POST";

    try {
      const response = await apiFetch(url, {
        method,
        body: formData,
      });
      if (!response.ok) {
        const message = await readError(response);
        console.error("Save failed:", response.status, response.statusText, message);
        throw new Error(message);
      }
      const result = await response.json();
      if (hasNewVideo) await attachVideoToPost(result.id || editPostId);
      if (hasNewVideo || removingSaved || replacesSavedVideo) await deleteReplacedVideo();
      // The user switched to another media type: the video uploaded in the meantime is not
      // part of this post, so delete it (frees the quota)
      if (!isVideoPost && videoUpload.status !== "idle") await videoUpload.discardVideo();
      setPostSaved(true);
      setSavedPostId(result.id);
      setSavedImageUrl(result.image_url);
      setSavedDocumentUrl(result.document_url || null);
    } catch (err) {
      console.error("Save post failed:", err);
      throw err;
    }
  };

  const goToSavedPost = () => {
    setDraftSaved(false);
    navigate("/draft", { state: { refresh: true, view: "list" } });
  };

  useEffect(() => {
    if (!draftSaved) return;
    const timer = setTimeout(goToSavedPost, 2000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftSaved]);

  useEffect(() => {
    if (accounts.length === 1) setLinkedInTarget(accounts[0].target);
  }, [accounts]);

  const persistPublishMetadata = async () => {
    const id = editPostId || savedPostId;
    if (!id) return;
    const formData = new FormData();
    formData.append("id", id);
    formData.append("post", convertHtmlToLinkedIn(stripDividers(previewText)));
    formData.append("hook", postSegments?.hook || "");
    formData.append("body", postSegments?.cuerpo || "");
    formData.append("cta", postSegments?.cta || "");
    formData.append("hashtags", JSON.stringify(postSegments?.hashtags || []));
    formData.append("target", linkedInTarget);
    formData.append("publication_date", formatLocalDateTime());
    formData.append("timezone", getTimezone());

    const response = await apiFetch(process.env.REACT_APP_UPDATE_POST_URL, {
      method: "PUT",
      body: formData,
    });
    if (!response.ok) throw new Error(await readError(response));
  };

  const handlePublish = async (confirmed = false) => {
    if (!confirmed) {
      setIsPublishSheetOpen(true);
      return;
    }
    if (!accounts.some((a) => a.target === linkedInTarget)) {
      setErrorHeader(t("scheduleModal.accountRequired"));
      return;
    }
    // Section D: a video post never goes through LinkedInPostPublisher (it would publish
    // the text only). Scheduling it for "now" lets the backend scheduler publish it.
    if (contentType === "video") {
      setErrorHeader("");
      setIsPublishing(true);
      try {
        await persistPublishMetadata();
        setPublishSuccess(true);
      } catch (err) {
        console.error("Scheduling the video post failed:", err);
        setErrorHeader(t("errors.publishFailed"));
      } finally {
        setIsPublishing(false);
      }
      return;
    }
    const payload = {
      oid: email,
      target: linkedInTarget,
      post_id: savedPostId,
      hook: postSegments?.hook || "",
      body: postSegments?.cuerpo || "",
      cta: postSegments?.cta || "",
      hashtags: postSegments?.hashtags || [],
    };

    if (contentType === "carousel" || contentType === "document") {
      if (savedDocumentUrl) {
        payload.document_url = savedDocumentUrl;
        payload.document_filename = documentFile?.name
        ? titleFromFilename(documentFile.name)
        : mediaTitleFrom(postSegments?.hook);
      }
    } else if (savedImageUrl) {
      payload.image_url = savedImageUrl;
    }

    setErrorHeader("");
    setIsPublishing(true);
    try {
      const response = await apiFetch(process.env.REACT_APP_PUBLISH_POST_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("Publish failed");
      try {
        await persistPublishMetadata();
      } catch (err) {
        console.error("Published, but failed to record metadata:", err);
      }
      setPublishSuccess(true);
    } catch (err) {
      console.error("Publish failed:", err);
      setErrorHeader(t("errors.publishFailed"));
    } finally {
      setIsPublishing(false);
    }
  };

  const handleLogoApplied = async (blob, isFromUpload = false) => {
    const key = isFromUpload ? "upload" : selectedImageIndex;

    // Capture this image's pre-logo original once, as an independent copy.
    if (!preLogoImages[key]) {
      if (isFromUpload && previewImages[0]) {
        const file = previewImages[0];
        setPreLogoImages((prev) => ({ ...prev, [key]: { file, url: URL.createObjectURL(file) } }));
      } else if (!isFromUpload && activeGeneratedImgUrl) {
        try {
          const res = await fetch(activeGeneratedImgUrl);
          const original = await res.blob();
          setPreLogoImages((prev) => ({ ...prev, [key]: { file: null, url: URL.createObjectURL(original) } }));
        } catch (err) {
          console.error("Couldn't keep the pre-logo image:", err);
        }
      }
    }

    if (isFromUpload) {
      setPreviewImages([new File([blob], "logo-composite.png", { type: "image/png" })]);
    } else {
      setGeneratedImages((prev) => {
        const next = [...prev];
        const old = next[selectedImageIndex];
        if (typeof old === "string" && old.startsWith("blob:")) URL.revokeObjectURL(old);
        next[selectedImageIndex] = URL.createObjectURL(blob);
        return next;
      });
      setIsGeneratedImageSelected(true);
      setPreviewImages([]);
    }
    setImageLocked(true);
    setPostSaved(false);
  };

  // Progress: the mobile flow maps its screens to stages; the desktop reports its own
  const MOBILE_STAGE = { 2: "text", 3: "text", 4: "media", 5: "media", 6: "media", 7: "media", 8: "publish", 9: "media" };
  const progressStage = editPostId
    ? null
    : isMobile
      ? (mobileStep === 1 ? (hasOwnText === true ? "text" : "start") : MOBILE_STAGE[mobileStep])
      : desktopStage;

  // Returning to media that already exists: has the text moved on since it was made?
  const handleReturnToMedia = (type) => {
    if (
      type === "single" &&
      imageMeta &&
      imageFingerprint(postSegments, imageMeta.format) !== imageMeta.fingerprint
    ) {
      setStaleType("single");
    }
    if (
      type === "carousel" &&
      carouselMeta &&
      carouselFingerprint(postSegments) !== carouselMeta
    ) {
      setStaleType("carousel");
    }
  };

  const canRegenInfographic =
    extractPhrases(postSegments?.cuerpo?.trim() || "").filter((p) => p.length > 5).length >= 2;

  return (
    <div className="flex flex-col min-h-screen bg-black-0">
      {showWelcome && <WelcomeModal onStart={handleStart} />}
      <Navbar
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        onOpenAccountModal={() => setIsAccountModalOpen(true)}
        onPublish={() => handlePublish()}
        canPublish={!!previewText && !isPublishing && postSaved}
        showPublish={postSaved}
        progressStage={progressStage}
      />
      <div className="flex flex-1 overflow-auto">
        <SideBar
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
          hasContent={(!!previewText || !!generatedHooks) && !postSaved}
          onBeforeConnect={handleBeforeConnectLinkedIn}
        />
         <main className="flex-1 flex flex-col md:justify-center md:max-w-[1600px] md:p-6 md:space-y-6 relative transition-all duration-300 md:ml-[max(125px,calc((100%_-_1600px)/2))]">
          {/* ===== PROGRESS (phones) — from 768px up it lives in the header ===== */}
          {isMobile && progressStage && (
            <div className="md:hidden">
              <ProgressBar variant="mobile" stage={progressStage} />
            </div>
          )}
          {/* Hidden until post performance metrics exist. Restore with <ScoreBoard scores={scores} /> */}
          {errorHeader && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
              <span className="block sm:inline">{errorHeader}</span>
            </div>
          )}

          <CreateFlowProvider key={resetKey} videoUpload={videoFlow}>
          {/* ===== DESKTOP ===== */}
          {!isMobile && (
            <div
              key={resetKey}
              className="hidden lg:grid grid-cols-2 border border-gradient-04 rounded-2xl"
            >
              <PostSelection
                onGenerateIdeasRequest={handleGenerateIdeasRequest}
                onProgressStage={setDesktopStage}
                generatedHooks={generatedHooks}
                onGeneratePosts={handleGeneratePosts}
                onGenerateFromDocument={handleGenerateFromDocument}
                imageLocked={imageLocked}
                onRegenerateReady={(fn) => { generateImageRef.current = fn; }}
                onGeneratingChange={(isGenerating) => setIsImageGenerating(isGenerating)}
                onImageGenerated={handleImageGenerated}
                generatedPosts={generatedPosts}
                editedPostContent={selectedPostContent}
                textConfirmed={textConfirmed}
                postSegments={postSegments}
                onPostIdSelect={(id) => setSelectedPostId(id)}
                isEditMode={!!editPostId}
                contentType={contentType}
                onContentTypeChange={setContentType}
                hasMedia={hasMedia}
                onHasMediaChange={setHasMedia}
                onSaveAsDraft={handleSaveAsDraft}
                onSchedulePost={() => setIsSaveModalOpen(true)}
                isSavingDraft={isSavingDraft}
                onTextConfirm={(value) => setTextConfirmed(value ?? true)}
                onImagesChange={handleImagesChange}
                onGenerateSelect={handleGenerateSelect}
                onDocumentSelect={handleDocumentSelect}
                onDeleteImage={handleDeleteImage}
                onDocumentDelete={handleDocumentDelete}
                documentFilename={documentFilename}
                postSaved={postSaved}
                onResetText={handleResetText}
                postLanguage={postLanguage}
                onPostLanguageChange={setPostLanguage}
                promptText={promptText}
                onPromptChange={setPromptText}
                onOpenAccount={() => setIsAccountModalOpen(true)}
                userEmail={email}
                videoTarget={linkedInTarget}
                hasOwnText={hasOwnText}
                onOwnTextModeChange={setHasOwnText}
                ownTextDraft={ownTextDraft}
                onOwnTextDraftChange={setOwnTextDraft}
                onUseOwnText={handleUseOwnText}
                onCreateCarousel={handleCreateCarousel}
                onReturnToMedia={handleReturnToMedia}
              />
              <TipsSection
                generatedContent={selectedPostContent}
                onTextChange={(text) => {
                  setPreviewText(text);
                  setPostSaved(false);
                  const temp = document.createElement("div");
                  temp.innerHTML = text;
                  temp.querySelectorAll("[data-divider]").forEach((el) => el.remove());
                  setSelectedPostContent(text);
                }}
                previewImages={previewImages}
                onPreviewClick={() => setIsPreviewOpen(true)}
                showGeneratedImageUI={isGeneratedImageSelected}
                isInsightsLoading={isInsightsLoading}
                onRegenerateClick={handleRegenerateText}
                onDeleteImage={handleDeleteImage}
                imageLocked={imageLocked}
                generatedImages={generatedImages}
                selectedImageIndex={selectedImageIndex}
                onSelectImage={setSelectedImageIndex}
                onUnlockStyle={handleUnlockStyle}
                onRegenerateImage={() => setRegenOpen(true)}
                generatedImagesCount={generatedImages.length}
                maxGeneratedImages={MAX_GENERATED_IMAGES}
                textConfirmed={textConfirmed}
                onTextConfirm={(value) => setTextConfirmed(value ?? true)}
                postSegments={postSegments}
                selectedPostId={selectedPostId}
                generatingImage={isImageGenerating}
                imageError={null}
                isEditMode={!!editPostId}
                onSegmentsChange={setPostSegments}
                onLogoApplied={handleLogoApplied}
                contentType={contentType}
                carouselSlides={carouselSlides}
                onSlidesChange={setCarouselSlides}
                onCarouselExportReady={(fn) => { carouselExportRef.current = fn; }}
                documentFilename={documentFilename}
                documentUrl={savedDocumentUrl}
                documentFile={documentFile}
                onDocumentSelect={handleDocumentSelect}
                onDocumentDelete={handleDocumentDelete}
                onVideoRemoved={() => { if (!editPostId) setTextConfirmed(false); }}
                onDownload={handleDownload}
                userEmail={email}
                onOpenAccount={() => setIsAccountModalOpen(true)}
                preLogoImage={preLogoImage}
                onCarouselPreviewState={setCarouselPreview}
                autoSplit={hasOwnText === true}
                onPickVideo={startVideoUpload}
              />
            </div>
          )}

          {/* ===== MOBILE ===== */}
          {isMobile && (
            <div className="lg:hidden flex-1 flex flex-col">
              {mobileStep === 1 && (
                <PostSelection
                  mobileViewStep={1}
                  onGeneratePosts={async (text) => {
                    await handleGeneratePosts(text);
                  }}
                  onGenerateFromDocument={handleGenerateFromDocument}
                  promptText={promptText}
                  onPromptChange={setPromptText}
                  isEditMode={!!editPostId}
                  contentType={contentType}
                  onContentTypeChange={setContentType}
                  postLanguage={postLanguage}
                  onPostLanguageChange={setPostLanguage}
                  hasOwnText={hasOwnText}
                  onOwnTextModeChange={setHasOwnText}
                  ownTextDraft={ownTextDraft}
                  onOwnTextDraftChange={setOwnTextDraft}
                  onUseOwnText={handleUseOwnText}
                  onStepChange={setMobileStep}
                  onResetText={handleResetText}
                  postSegments={postSegments}
                />
              )}
              {mobileStep === 2 && (
                <PostSelection
                  mobileViewStep={2}
                  onStepChange={setMobileStep}
                  onResetText={handleResetText}
                  generatedHooks={generatedHooks}
                  selectedPostId={selectedPostId}
                  onPostIdSelect={(id) => setSelectedPostId(id)}
                  onGenerateIdeasRequest={(id) => handleGenerateIdeasRequest(id)}
                  onConfirm={() => setMobileStep(3)}
                  onBack={() => {
                    setGeneratedHooks(null);
                    setGeneratedPosts(null);
                    setMobileStep(1);
                  }}
                  postSegments={postSegments}
                  isEditMode={!!editPostId}
                  contentType={contentType}
                  onContentTypeChange={setContentType}
                  hasMedia={hasMedia}
                  onHasMediaChange={setHasMedia}
                />
              )}
                            {/* STEP 3 · Text editor only */}
              {mobileStep === 3 && (
                <TipsSection
                  mobileViewStep={3}
                  generatedContent={selectedPostContent}
                  onTextChange={(text) => {
                    setPreviewText(text);
                    setPostSaved(false);
                    const temp = document.createElement("div");
                    temp.innerHTML = text;
                    temp.querySelectorAll("[data-divider]").forEach((el) => el.remove());
                    setSelectedPostContent(text);
                  }}
                  postSegments={postSegments}
                  onSegmentsChange={setPostSegments}
                  onPreviewClick={() => setIsPreviewOpen(true)}
                  onDownload={handleDownload}
                  isEditMode={!!editPostId}
                  onConfirm={() => setMobileStep(4)}
                  onBack={() => setMobileStep(hasOwnText ? 1 : 2)}
                  autoSplit={hasOwnText === true}
                />
              )}

              {/* STEPS 4–6, 8 · Questions and All set */}
              {[4, 5, 6, 8, 9].includes(mobileStep) && (
                <PostSelection
                  mobileViewStep={mobileStep}
                  onStepChange={setMobileStep}
                  hasMedia={hasMedia}
                  onHasMediaChange={setHasMedia}
                  contentType={contentType}
                  onContentTypeChange={setContentType}
                  onTextConfirm={(value) => setTextConfirmed(value ?? true)}
                  onImagesChange={handleImagesChange}
                  onGenerateSelect={handleGenerateSelect}
                  onDocumentSelect={handleDocumentSelect}
                  onDeleteImage={handleDeleteImage}
                  onDocumentDelete={handleDocumentDelete}
                  onImageGenerated={handleImageGenerated}
                  onGeneratingChange={(isGenerating) => setIsImageGenerating(isGenerating)}
                  onRegenerateReady={(fn) => { generateImageRef.current = fn; }}
                  onSaveAsDraft={handleSaveAsDraft}
                  onSchedulePost={() => setIsSaveModalOpen(true)}
                  onResetText={handleResetText}
                  isSavingDraft={isSavingDraft}
                  postSaved={postSaved}
                  documentFilename={documentFilename}
                  imageLocked={imageLocked}
                  generatedHooks={generatedHooks}
                  generatedPosts={generatedPosts}
                  selectedPostId={selectedPostId}
                  editedPostContent={selectedPostContent}
                  postSegments={postSegments}
                  generatedImagesCount={generatedImages.length}
                  maxGeneratedImages={MAX_GENERATED_IMAGES}
                  isEditMode={!!editPostId}
                  postLanguage={postLanguage}
                  onPostLanguageChange={setPostLanguage}
                  onOpenAccount={() => setIsAccountModalOpen(true)}
                  userEmail={email}
                  videoTarget={linkedInTarget}
                  onCreateCarousel={handleCreateCarousel}
                  onReturnToMedia={handleReturnToMedia}
                />
              )}

              {/* STEP 7 · Media editor only */}
              {mobileStep === 7 && (
                <TipsSection
                  mobileViewStep={7}
                  generatedContent={selectedPostContent}
                  previewImages={previewImages}
                  showGeneratedImageUI={isGeneratedImageSelected}
                  isInsightsLoading={isInsightsLoading}
                  onDeleteImage={handleDeleteImage}
                  imageLocked={imageLocked}
                  generatedImages={generatedImages}
                  selectedImageIndex={selectedImageIndex}
                  onSelectImage={setSelectedImageIndex}
                  onUnlockStyle={handleUnlockStyle}
                  onRegenerateImage={() => setRegenOpen(true)}
                  generatedImagesCount={generatedImages.length}
                  maxGeneratedImages={MAX_GENERATED_IMAGES}
                  onLogoApplied={handleLogoApplied}
                  contentType={contentType}
                  carouselSlides={carouselSlides}
                  onSlidesChange={setCarouselSlides}
                  onCarouselExportReady={(fn) => { carouselExportRef.current = fn; }}
                  documentFilename={documentFilename}
                  documentUrl={savedDocumentUrl}
                  documentFile={documentFile}
                  onDocumentSelect={handleDocumentSelect}
                  onVideoRemoved={() => { if (editPostId) return; setTextConfirmed(false); setMobileStep(9); }}
                  onPreviewClick={() => setIsPreviewOpen(true)}
                  onDownload={handleDownload}
                  postSegments={postSegments}
                  selectedPostId={selectedPostId}
                  isEditMode={!!editPostId}
                  onConfirm={async () => {
                    if (contentType === "carousel") {
                      const file = await carouselExportRef.current?.(
                        mediaFilenameFrom(postSegments?.hook)
                      );
                      setCarouselPdfFile(file || null);
                    }
                    setMobileStep(8);
                  }}
                   onBack={() => {
                    setCarouselPdfFile(null);
                    setMobileStep(5);
                  }}
                  userEmail={email}
                  onOpenAccount={() => setIsAccountModalOpen(true)}
                  preLogoImage={preLogoImage}
                  onCarouselPreviewState={setCarouselPreview}
                  onPickVideo={startVideoUpload}
                />
              )}
            </div>
          )}
          </CreateFlowProvider>

          <PreviewModal
            open={isPreviewOpen}
            onClose={() => setIsPreviewOpen(false)}
            text={previewText}
            images={previewImages}
            showGeneratedImage={isGeneratedImageSelected}
            generatedImage={activeGeneratedImgUrl}
            contentType={contentType}
            carouselSlides={carouselSlides}
            documentFile={documentFile}
            documentUrl={savedDocumentUrl}
            documentFilename={documentFilename}
            videoUrl={videoFlow.previewUrl || videoFlow.saved?.video_url}
            videoPosterUrl={videoFlow.posterPreviewUrl || videoFlow.saved?.video_poster_url}
            onDownload={handleDownload}
            carouselPreview={carouselPreview}
          />
        </main>
      </div>
      <Footer />
      <ScheduleModal
        isOpen={isSaveModalOpen}
        onClose={() => setIsSaveModalOpen(false)}
        onSchedule={handleSavePost}
        onSaved={() => {
          setIsSaveModalOpen(false);
          navigate("/draft", { state: { refresh: true, view: "calendar" } });
        }}
        isEditMode={!!editPostId}
        accounts={accounts}
        defaultTarget={linkedInTarget}
        initialDate={editPostDateTime}
      />
      <UserAccountModal
        userName=""
        setUserName={() => {}}
        userEmail={email}
        isOpen={isAccountModalOpen}
        onClose={() => setIsAccountModalOpen(false)}
        onBeforeConnect={handleBeforeConnectLinkedIn}
      />
      {isInsightsLoading && (
        <>
          <div className="fixed inset-0 bg-white/80 z-40" aria-hidden="true" />
          <div
            className="fixed inset-0 flex items-center justify-center z-50"
            aria-live="polite"
            aria-busy="true"
          >
            <FeelimThinkingAnimation />
          </div>
        </>
      )}
      {isImageGenerating && (
        <>
          <div className="fixed inset-0 bg-white/80 z-40" aria-hidden="true" />
          <div
            className="fixed inset-0 flex items-center justify-center z-50"
            aria-live="polite"
            aria-busy="true"
          >
            <FeelimImageAnimation />
          </div>
        </>
      )}
      <PublishBottomSheet
        isOpen={isPublishSheetOpen}
        onClose={() => setIsPublishSheetOpen(false)}
        onPublish={async () => { await handlePublish(true); setIsPublishSheetOpen(false); }}
        isPublishing={isPublishing}
        accounts={accounts}
        selectedAccount={linkedInTarget}
        onSelectAccount={(target) => setLinkedInTarget(target)}
        isLinkedInConnected={isLinkedInConnected}
        onOpenAccountModal={() => setIsAccountModalOpen(true)}
      />
      {publishSuccess && (
        <div className="fixed inset-0 bg-black-900 bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm mx-4 flex flex-col items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-black-800">{t(contentType === "video" ? "video.publishing.title" : "publish.successTitle")}</h2>
            <p className="text-black-600 text-center text-lg">{t(contentType === "video" ? "video.publishing.message" : "publish.successMessage")}</p>
            <div className="flex flex-col gap-3 w-full mt-2">
              {contentType !== "video" && (
                <a
                  href="https://www.linkedin.com/feed/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0A66C2] hover:bg-[#004182] text-white rounded text-lg font-bold transition w-full"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452H17.21v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.985V9h3.105v1.561h.044c.432-.818 1.49-1.681 3.067-1.681 3.28 0 3.885 2.159 3.885 4.964v6.608zM5.337 7.433a1.805 1.805 0 1 1 0-3.61 1.805 1.805 0 0 1 0 3.61zM6.955 20.452H3.72V9h3.235v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                  {t("publish.viewOnLinkedIn")}
                </a>
              )}.
              <Button variant="ghost" onClick={() => setPublishSuccess(false)} className="w-full justify-center">
                {t("buttons.close")}
              </Button>
            </div>
          </div>
        </div>
      )}
      {showMediaBackConfirm && (
        <div className="fixed inset-0 bg-black-900 bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-4 w-full max-w-[400px] shadow-xl">
            <h3 className="text-lg font-bold mb-4">{t("dialog.changeMedia.title")}</h3>
            <p className="bg-link-100 text-black-800 text-lg px-2 mb-4">
              {t("dialog.changeMedia.message")}
            </p>
            <div className="flex flex-col sm:flex-row justify-end gap-3 text-sm sm:text-lg font-bold">
              <Button
                variant="ghost"
                onClick={() => setShowMediaBackConfirm(false)}
                className="w-full sm:w-auto"
              >
                {t("buttons.cancel")}
              </Button>
              <Button variant="dark" onClick={goBackToMediaTypeStep} className="w-full sm:w-auto">
                {t("buttons.continue")}
              </Button>
            </div>
          </div>
        </div>
      )}
      <StaleMediaModal
        open={staleType === "single"}
        title={t("mediaModals.staleImage.title")}
        message={t("mediaModals.staleImage.message")}
        confirmLabel={t("mediaModals.staleImage.confirm")}
        keepLabel={t("mediaModals.staleImage.keep")}
        onKeep={() => setStaleType(null)}
        onConfirm={() => {
          setStaleType(null);
          setRegenOpen(true);
        }}
      />
      <StaleMediaModal
        open={staleType === "carousel"}
        title={t("mediaModals.staleCarousel.title")}
        message={t("mediaModals.staleCarousel.message")}
        confirmLabel={t("mediaModals.staleCarousel.confirm")}
        keepLabel={t("mediaModals.staleCarousel.keep")}
        onKeep={() => setStaleType(null)}
        onConfirm={() => {
          setStaleType(null);
          handleCreateCarousel();
        }}
      />
      <RegenerateImageModal
        open={regenOpen}
        onClose={() => setRegenOpen(false)}
        userEmail={email}
        canCreateInfographic={canRegenInfographic}
        onOpenAccount={() => setIsAccountModalOpen(true)}
        onConfirm={(format, colors) => {
          setRegenOpen(false);
          generateImageRef.current?.(format, colors);
        }}
      />
      {draftSaved && (
        <div className="fixed inset-0 bg-black-900 bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm mx-4 flex flex-col items-center gap-4">
            <div className="text-4xl">✅</div>
            <h2 className="text-xl font-bold text-black-800">
              {t("saveModal.successTitle")}
            </h2>
            <p className="text-black-600 text-center text-lg">
              {t("saveModal.successMessage")}
            </p>
            <Button variant="ghost" onClick={goToSavedPost} className="w-full justify-center">
              {t("buttons.close")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;